import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Form, Modal, Skeleton } from 'antd'
import { useSelector } from 'react-redux'
import {
  getOrdersFormAlert,
  getOrdersFormData,
  getOrdersFormError,
  getOrdersFormLoading,
  getVisibleFormData,
} from '../../selectors'
import { HStack, VStack } from '../../../../../ui/Stack'
import { DefaultJsonForm } from '../../../../../components/v2/default-json-form'
import { useSaveResult } from '../../hooks/useSaveResult'
import { useNavigate } from 'react-router-dom'
import { isEqual, cloneDeep } from 'lodash'
import { useMediaQuery } from 'react-responsive'
import { useAppDispatch } from '../../../../../hooks/useAppDispatch/useAppDispatch'
import { WindrawOrderPanel } from '../windraw-order-panel'
import { useOrderFormSave } from '../../order-form-save-context'

interface OrdersFormsProps {
  className?: string
  id: string | number
}

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 8 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 24 },
  },
}

export const Forms: React.FC<OrdersFormsProps> = memo((props: OrdersFormsProps) => {
  const { id } = props
  const [form] = Form.useForm()
  const dispatch = useAppDispatch()
  const ordersFormData = useSelector(getOrdersFormData)
  const ordersFormIsLoading = useSelector(getOrdersFormLoading)
  const ordersFormError = useSelector(getOrdersFormError)
  const ordersFormAlert = useSelector(getOrdersFormAlert)
  const visibleFormData = useSelector(getVisibleFormData)
  const { saveResult } = useSaveResult()
  const { registerFlush } = useOrderFormSave()
  const navigate = useNavigate()
  const formRef = useRef<typeof visibleFormData>()
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const [prevFormData, setPrevFormData] = useState<any>(undefined)
  const hasThreeFieldColumns = useMediaQuery({ minWidth: 1800 })
  const hasTwoFieldColumns = useMediaQuery({ minWidth: 1400 })
  const groupColumns = hasThreeFieldColumns ? 3 : hasTwoFieldColumns ? 2 : 1

  useEffect(() => {
    if (visibleFormData && ordersFormData) {
      formRef.current = visibleFormData
      setPrevFormData(cloneDeep(visibleFormData.resultObject))
    }
  }, [visibleFormData, ordersFormData])

  const saveCase = useCallback(
    async (isNextStage = false, stageCode?: string) => {
      await saveResult(
        id,
        formRef.current,
        ordersFormData?.stages?.current,
        false,
        () => {},
        isNextStage,
        stageCode,
      )
    },
    [id, saveResult, ordersFormData],
  )

  const flushPendingSave = useCallback(async () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
      saveTimeoutRef.current = undefined
    }
    await saveCase()
  }, [saveCase])

  useEffect(() => {
    registerFlush(flushPendingSave)
    return () => registerFlush(null)
  }, [registerFlush, flushPendingSave])

  useEffect(() => {
    const redirectUrl = ordersFormData?.redirectUrl
    if (redirectUrl) {
      navigate(redirectUrl)
    }
  }, [ordersFormData])

  const scheduleSaveCase = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }
    saveTimeoutRef.current = setTimeout(() => {
      saveTimeoutRef.current = undefined
      void saveCase()
    }, 1000)
  }, [saveCase])

  const onChangeForm = useCallback(
    async ({ formData }) => {
      if (!formRef.current) {
        return
      }
      // проверяет, являются ли данные идентичными
      if (isEqual(prevFormData, formData)) {
        return
      }
      // если инициализация - заполняет промежуточное состояние
      if (!prevFormData && !isEqual(prevFormData, formData)) {
        setPrevFormData({ ...formData })
        return
      }

      setPrevFormData({ ...formData })
      formRef.current.resultObject = formData
      scheduleSaveCase()
    },
    [formRef, scheduleSaveCase, prevFormData],
  )

  useEffect(() => {
    return () => {
      setPrevFormData(undefined)
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (ordersFormAlert) {
      Modal.warning({
        title: 'Предупреждение',
        content: ordersFormAlert,
        onOk: () => {
          dispatch({ type: 'ORDERS_FORM_ALERT', payload: null })
        },
      })
    }
  }, [ordersFormAlert, dispatch])

  if (ordersFormError && !ordersFormIsLoading && !ordersFormData) {
    return (
      <HStack gap={'8'} className={'mapWrapper'}>
        При выполнении запроса возникла ошибка. Попробуйте перезагрузить страницу или открыть
        карточку повторно.
      </HStack>
    )
  }

  return (
    <>
      <HStack gap={'8'} max>
        <Form
          {...formItemLayout}
          layout="horizontal"
          form={form}
          scrollToFirstError={true}
          style={{ width: '100%' }}
          component="div"
        >
          {ordersFormError && !ordersFormIsLoading ? (
            <div className={'alertWrapper'}>
              <Alert
                message="При выполнении операции возникла ошибка:"
                showIcon
                type="error"
                description={ordersFormError}
              />
            </div>
          ) : null}
          {!ordersFormIsLoading ? (
            <VStack gap={'16'} max align={'stretch'} className={'ordersForm'}>
              {visibleFormData && (
                <DefaultJsonForm
                  formData={visibleFormData.resultObject}
                  schema={visibleFormData.resultSchema}
                  onChange={onChangeForm}
                  orientation="horizontal"
                  formLayoutClassName="ordersForm__field-grid"
                  groupColumns={groupColumns}
                  trailingContent={
                    <WindrawOrderPanel
                      orderId={id}
                      productionNumber={
                        (visibleFormData.resultObject as { production_number?: string })
                          ?.production_number
                      }
                    />
                  }
                />
              )}
            </VStack>
          ) : (
            <Skeleton active />
          )}
        </Form>
      </HStack>
    </>
  )
})

Forms.displayName = 'OrderForm'
